import { GetServerSideProps } from 'next';
import { addApolloState } from '@lib/apollo/apolloClient';
import { getMerchGroups, MERCH_GROUP_URL_PREFIX } from '@components/shared/shop/model/MerchGroup';
import getShopRedirect from '@components/pages/ShopV3/util/redirects';
import {
  getSelectedShopGroupChain,
  getShopGroupBySlug,
  getTopLevelShopGroups,
  setSelectedShopGroup,
  ShopGroupType
} from '@components/shared/shop/model/ShopGroup';
import { allFulfilled, getFulfilledValue } from '@lib/promise';
import { IShopPageData, ShopPageType } from '@components/pages/ShopV3/model/ShopPage';
import formatShopSuggestions
  from '@components/pages/ShopV3/components/ShopSuggestions/formatShopSuggestions';
import {
  fetchShopNavigationMenu,
  IShopNavigationMenu
} from '@components/shared/shop/ShopNavigationMenu';
import {
  getSelectedVendorGroup,
  getSelectedVendorGroupChain,
  getVendorGroups
} from '@components/shared/shop/model/VendorGroup';
import { IMerchGroup } from '@models/MerchGroup/i-merch-group';
import {
  createSearchCriteria,
  getApolloForSearch,
  parseFacets,
  productSearch
} from '@components/pages/ShopV3/util/search';
import {
  IMerchGroupCategory,
  MERCH_GROUP_CATEGORY_URL_PREFIX
} from '@components/shared/shop/model/MerchGroupCategory';
import ShopPage from '@components/pages/ShopV3/ShopPage';
import { getQueryString } from '@lib/url';

export default ShopPage;

const HOME_SLUG = 'home';

export const getServerSideProps: GetServerSideProps = async (context) => {
  const {
    query,
    params,
    resolvedUrl,
    previewData,
  } = context;

  const redirectUrl = getShopRedirect(resolvedUrl);
  if (redirectUrl) {
    return { redirect: { destination: redirectUrl, permanent: true } };
  }

  let slugs: string[] = [];
  if (params?.slugs) {
    slugs = Array.isArray(params.slugs) ? params.slugs : [params.slugs];
  }
  const slug = Array.isArray(slugs) ? slugs.join('/') : slugs;

  if (slug === HOME_SLUG) {
    return {
      redirect: {
        permanent: true,
        destination: `/shop/search${(getQueryString(resolvedUrl, true))}`
      }
    };
  }

  const apolloClient = getApolloForSearch();

  const selectedShopGroupBySlug = await getShopGroupBySlug(apolloClient, slug);

  if (selectedShopGroupBySlug?.__typename === ShopGroupType.MERCH_GROUP) {
    const selectedMerchGroup = selectedShopGroupBySlug as IMerchGroup;
    return {
      redirect: {
        permanent: true,
        destination: `${MERCH_GROUP_URL_PREFIX}/${selectedMerchGroup.slug}`
      }
    };
  } else if (selectedShopGroupBySlug?.__typename === ShopGroupType.MERCH_GROUP_CATEGORY) {
    const selectedMerchGroupCategory = selectedShopGroupBySlug as IMerchGroupCategory;
    return {
      redirect: {
        permanent: true,
        destination: `${MERCH_GROUP_CATEGORY_URL_PREFIX}/${selectedMerchGroupCategory.slug}`
      }
    };
  }

  const [
    merchGroups,
    shopGroupsResult,
  ] = await Promise.allSettled([
    getMerchGroups(apolloClient),
    getTopLevelShopGroups(apolloClient),
  ]);

  const shopGroups = setSelectedShopGroup(
    getFulfilledValue(shopGroupsResult),
    selectedShopGroupBySlug
  );

  const vendorGroups = getVendorGroups(slugs);
  const selectedVendorGroup = getSelectedVendorGroup(vendorGroups) || null;

  if (!selectedVendorGroup) {
    // Slug didn't match anything we know about
    return {
      redirect: {
        permanent: false,
        destination: '/shop'
      }
    };
  }

  const shopPageData: IShopPageData = {
    title: selectedVendorGroup.title,
    featuredItems: [],
  };
  const shopPageType = ShopPageType.VENDOR_GROUP;

  const searchCriteria = createSearchCriteria({
    query,
    selectedVendorGroup,
  });

  const [searchResponse, shopNavigationMenu] = await allFulfilled([
    productSearch(apolloClient, searchCriteria),
    fetchShopNavigationMenu({
      previewData,
      apolloClient,
      merchGroups: getFulfilledValue(merchGroups),
      merchGroupCategories: shopGroups,
    })
  ]);

  const selectedShopGroupChain = getSelectedShopGroupChain(shopGroups);

  return addApolloState(apolloClient, {
    props: {
      query: searchCriteria.query || null,
      slugs: slugs || null,
      shopPageType,
      searchCriteria,
      searchFilters: {
        shopGroups,
        selectedMerchGroup: null,
        selectedShopGroupChain: selectedShopGroupChain || [],
        selectedVendorGroupChain: getSelectedVendorGroupChain(vendorGroups) || [],
      },
      searchResults: {
        ...searchResponse.data.searchShopProducts,
        facets: parseFacets(searchResponse.data.searchShopProducts.facets),
      },
      shopPageData,
      shopNavigationMenu,
      footerImages: formatShopSuggestions(
        shopNavigationMenu.map((m: IShopNavigationMenu) => m.option)
      ),
    },
  });
};
